import isNil from 'lodash/isNil';

import {
  Constants,
  ProviderType,
  ProviderTypes,
  ReverseCoverageTypesCodes,
} from '../Constants';
import { ConstantsRoutes } from '../ConstantsRoutes';
import { convertProviderTypeToAdobeType } from '../Utils/adobeTrackUtils/adobeTrackUtils';
import { checkTerminationDate } from '../Utils/terminateDateIndicatorUtils';

export function setPinOnMapFn(updatePin, practitioner) {
  if (updatePin) {
    updatePin(practitioner);
  }
}

export function inOrOutOfNetwork(status) {
  return status ? 'In-network' : 'Not-In-Network';
}

export function shouldSuppressVirtualCare(
  isVirtualCareQuickCareSuppressed: boolean,
  coverageType: string,
  isVirtualCareUnetRailroadSuppressed: boolean
) {
  return (
    isVirtualCareQuickCareSuppressed ||
    (coverageType !== ReverseCoverageTypesCodes['BEHAVIORAL HEALTH'] &&
      isVirtualCareUnetRailroadSuppressed)
  );
}

export function ratingsOnClickHandler(
  healthGradeRating: string | undefined | null,
  handleDetailsOnClick: any
) {
  return healthGradeRating !== null ? handleDetailsOnClick : undefined;
}

export function shouldShowCursorOnRatings(
  healthGradeRating: string | undefined | null
) {
  return healthGradeRating !== null ? 'pointer' : '';
}

export function isIFPOrganizationCheck(
  providerType: string | undefined,
  isIFPMember: boolean
) {
  return providerType === ProviderType.ORGANIZATION && isIFPMember;
}

export function displayTier1Identifier(isTier1Provider) {
  if (isNil(isTier1Provider)) {
    return false;
  }
  return isTier1Provider;
}

export function displayTier2Identifier(
  isTier2Provider,
  isIFPTieredPlan: boolean
) {
  if (isNil(isTier2Provider)) {
    return false;
  }
  return isTier2Provider && isIFPTieredPlan;
}

export const getIsSmartChoicePriceCheck = (
  isSmartChoicePractitioner: boolean | undefined,
  isTieredPlan: boolean | undefined,
  isAcoBenefitPlan: boolean | undefined,
  acoProvider: boolean | undefined,
  isCustomPreferredPolicy: boolean | undefined
) =>
  !isSmartChoicePractitioner ||
  (isTieredPlan === false &&
    !isAcoBenefitPlan &&
    acoProvider === false &&
    !isCustomPreferredPolicy);

export const isPremiumCareDisplayEligible = (
  premiumCareFeatureFlag,
  isPremiumCarePhysician
) => premiumCareFeatureFlag && isPremiumCarePhysician;

export function displayPremiumCareIdentifier(
  premiumCareFeatureFlag,
  isPremiumCarePhysician,
  isTieredProvider,
  isDisplayTier2Identifier,
  memberHasRPK,
  isCustomPreferredPolicy,
  isMultipleBadgeCardViewEnabled = false
) {
  if (
    isNil(premiumCareFeatureFlag) ||
    isNil(isPremiumCarePhysician) ||
    isNil(isTieredProvider) ||
    isNil(isDisplayTier2Identifier) ||
    isNil(memberHasRPK) ||
    isNil(isCustomPreferredPolicy)
  ) {
    return false;
  }

  return (
    isPremiumCareDisplayEligible(
      premiumCareFeatureFlag,
      isPremiumCarePhysician
    ) &&
    (isMultipleBadgeCardViewEnabled ||
      !(isTieredProvider || isDisplayTier2Identifier || memberHasRPK)) &&
    !isCustomPreferredPolicy
  );
}

export const initializeCardVariantsPopupState = (CardInfoVariants) =>
  Object.values(CardInfoVariants).reduce(
    (acc: { [key: string]: boolean }, variant: string) => {
      acc[variant] = false;
      return acc;
    },
    {}
  );

export const getLinkNameForSearchResultEvent = (
  providerTypeInfo?: string,
  clickSection?: string
) => {
  let linkName: string | undefined;

  if (clickSection && clickSection !== '') {
    linkName = clickSection;
  } else {
    linkName = `${convertProviderTypeToAdobeType(providerTypeInfo)} name`;
  }
  return linkName;
};

export const getLinkPositionIndex = (linkPosition?: number) => {
  if (linkPosition !== undefined) {
    return linkPosition + 1;
  }
  return linkPosition;
};

export const handlePatientReviewsView = (
  enableCost,
  practitioner,
  displayVirtualVisit
) => {
  let view = true;
  if (!(enableCost && !!practitioner?.costIndicator)) {
    view = false;
  }
  if (!(enableCost && !!practitioner?.costIndicator) && !displayVirtualVisit) {
    view = true;
  }
  if (!displayVirtualVisit) {
    view = true;
  }
  return view;
};

export const getDetailsRoute = (sectionTypeDerived) => {
  let detailsRoute;
  switch (sectionTypeDerived) {
    case Constants.RESULT_SECTION.FACILITY_CAPITAL:
      detailsRoute = ConstantsRoutes.FACILITY_DETAILS.path;
      break;
    case Constants.RESULT_SECTION.PROVIDER_GROUPS:
      detailsRoute = ConstantsRoutes.PROVIDER_GROUP_DETAILS.path;
      break;
    default:
      detailsRoute = ConstantsRoutes.PROVIDER_DETAILS.path;
      break;
  }
  return detailsRoute;
};

export const shouldDisplayVirtualVisit = (
  virtualCareIndicatorEnabled: boolean,
  suppressVirtualCare: boolean,
  address: any,
  isIFPOrganization: boolean,
  virtualCareOffered: boolean,
  providerType: string | undefined
) =>
  virtualCareIndicatorEnabled &&
  !suppressVirtualCare &&
  address &&
  !isIFPOrganization &&
  virtualCareOffered &&
  (providerType?.toLowerCase() === ProviderTypes.PRACTITIONER.toLowerCase() ||
    providerType?.toLowerCase() === ProviderTypes.MEDICALGROUP.toLowerCase());

export const getMaxBadgeCount = (
  dataCardProviders,
  isPlatinumProviderIdentifierEnabled: boolean,
  displayTerminationDateEnabled: boolean,
  displayVirtualVisit: boolean,
  showCostEstimates: boolean,
  shouldDisplayNetworkStatus: boolean
): number => {
  if (!dataCardProviders || dataCardProviders.length === 0) {
    return 0;
  }
  return dataCardProviders.reduce((maxCount, dataCardProvider) => {
    const badgeConditions = [
      isPlatinumProviderIdentifierEnabled &&
        dataCardProvider?.isPlatinumProvider,
      checkTerminationDate(
        displayTerminationDateEnabled,
        dataCardProvider?.networkEndDate
      ),
      (dataCardProvider?.costIndicator && showCostEstimates) ||
        displayVirtualVisit,
      shouldDisplayNetworkStatus,
    ];

    const validBadgeCount = badgeConditions.filter(Boolean).length;
    return Math.max(maxCount, validBadgeCount);
  }, 0);
};

export const infoCardHeight = (maxBadgeCount = 0) => {
  const cardHeights = {
    4: {
      height: '305px',
      '@screen < $sm': { width: '320px', height: '265px' },
    },
    3: {
      height: '280px',
      '@screen < $sm': { width: '320px', height: '240px' },
    },
    2: {
      height: '270px',
      '@screen < $sm': { width: '320px', height: '230px' },
    },
    1: {
      height: '250px',
      '@screen < $sm': { width: '320px', height: '210px' },
    },
    0: {
      height: '230px',
      '@screen < $sm': { width: '320px', height: '190px' },
    },
  };

  return cardHeights[maxBadgeCount];
};

/**
 * Shared core logic for checkbox state changes in DataCard and DataCardList.
 * Handles state update, provider selection, adobe tracking and index tracking.
 */
export const applyCheckboxChange = (
  provider: any,
  index: number,
  selectedCheckbox: any,
  setSelectedCheckbox: (fn: (prev: any) => any) => void,
  addSelectedProvider: (p: any) => void,
  adobeLinkTrackEventCheckBox: (checked: boolean, index: number) => void,
  setLastSelectedIndex: (i: number) => void
) => {
  setSelectedCheckbox((prevState: any) => ({
    checked: {
      ...prevState.checked,
      [provider?.locationId]: !prevState.checked[provider?.locationId],
    },
  }));
  addSelectedProvider(provider);
  adobeLinkTrackEventCheckBox(
    selectedCheckbox?.checked[provider?.locationId],
    index
  );
  setLastSelectedIndex(index);
};
